import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

/** Rótulo de localidade (município/UF) — usado quando várias cidades estão selecionadas. */
export const formatFeriadoLocalidade = (feriado) => {
  if (!feriado) return null;

  if (feriado.tipo === 'MUNICIPAL') {
    if (feriado.municipioNome && feriado.uf) {
      return `${feriado.municipioNome} / ${feriado.uf}`;
    }
    if (feriado.municipioNome) return feriado.municipioNome;
    if (feriado.codigoIbge) return String(feriado.codigoIbge);
  }

  if (feriado.tipo === 'ESTADUAL' && feriado.uf) {
    return feriado.uf;
  }

  return null;
};

export const formatFeriadoTooltip = (feriado, { showLocalidade = false } = {}) => {
  const localidade = showLocalidade ? formatFeriadoLocalidade(feriado) : null;
  if (localidade) return `${feriado.nome} — ${localidade}`;
  return feriado.nome;
};

/** Chave estável para agrupar feriados em "Próximos feriados" (data + nome + tipo). */
export const buildProximosFeriadoGroupKey = (feriado) => {
  const nome = (feriado.nome || '').trim().toLowerCase();
  return `${moment(feriado.data).format('YYYY-MM-DD')}|${nome}|${feriado.tipo}`;
};

/** Agrupa feriados com mesma data, nome e tipo — preserva ordem da primeira ocorrência. */
export const groupProximosFeriados = (feriados) => {
  const map = new Map();
  (feriados || []).forEach((f) => {
    const key = buildProximosFeriadoGroupKey(f);
    if (!map.has(key)) {
      map.set(key, { data: f.data, nome: f.nome, tipo: f.tipo, items: [] });
    }
    map.get(key).items.push(f);
  });
  return Array.from(map.values());
};

/** Localidades únicas de um grupo (ordem de aparição). */
export const getGroupedLocalidades = (items) => {
  const seen = new Set();
  const result = [];
  (items || []).forEach((f) => {
    const loc = formatFeriadoLocalidade(f);
    if (loc && !seen.has(loc)) {
      seen.add(loc);
      result.push(loc);
    }
  });
  return result;
};

/**
 * Monta texto resumido do subtítulo de localidades.
 * 4+ itens: 2 primeiros + "e mais N municípios"; tooltip sempre com lista completa.
 */
export const buildGroupedLocalidadesDisplay = (localidades) => {
  const all = localidades || [];
  if (!all.length) return null;

  const tooltipText = all.join(', ');
  if (all.length <= 3) {
    return { all, preview: null, overflowCount: 0, tooltipText };
  }

  return {
    all,
    preview: all.slice(0, 2).join(', '),
    overflowCount: all.length - 2,
    tooltipText,
  };
};

export const formatGroupedFeriadoTooltip = (group, { showLocalidade = false } = {}) => {
  if (!group?.items?.length) return '';
  if (group.items.length === 1) {
    return formatFeriadoTooltip(group.items[0], { showLocalidade });
  }
  if (!showLocalidade) return group.nome;
  const localidades = getGroupedLocalidades(group.items);
  if (!localidades.length) return group.nome;
  return `${group.nome} — ${localidades.join(', ')}`;
};

/** Agrupa feriados por data (chave `YYYY-MM-DD`) — mesma lógica serve mês e semana. */
export const groupFeriadosByDate = (feriados) => {
  const map = new Map();
  (feriados || []).forEach((feriado) => {
    const key = moment(feriado.data).format('YYYY-MM-DD');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(feriado);
  });
  return map;
};

/** Dias (moment) a renderizar na grade mensal — inclui padding de semanas anterior/posterior. */
export const buildMonthDays = (referenceDate) => {
  const start = moment(referenceDate).startOf('month').startOf('week');
  const end = moment(referenceDate).endOf('month').endOf('week');
  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    days.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return days;
};

/** Dias (moment) da semana corrente ao redor de `referenceDate`. */
export const buildWeekDays = (referenceDate) => {
  const start = moment(referenceDate).startOf('week');
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    days.push(start.clone().add(i, 'day'));
  }
  return days;
};

/**
 * Paleta fixa por tipo de feriado (não segue theme.palette.*) — replica os tons do protótipo
 * (docs/calendario-feriados-v2-prototipo.html: --blue-main, --purple-main, --orange-main) para
 * um visual mais "clean" que as cores saturadas padrão do MUI (info/secondary/warning).
 */
export const TIPO_COLOR = {
  NACIONAL: { main: '#2970FF', light: '#F5F8FF', dark: '#004EEB' },
  ESTADUAL: { main: '#9E77ED', light: '#F9F5FF', dark: '#6941C6' },
  MUNICIPAL: { main: '#F97316', light: '#FFF7ED', dark: '#C2410C' },
};

export const TIPO_LABEL = {
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
};

/** sx pronto para um Chip MUI com fundo claro + texto escuro na cor do tipo (ou cinza p/ inválido/desconhecido). */
export const tipoChipSx = (tipo) => {
  const cor = TIPO_COLOR[tipo];
  if (!cor) return {};
  return {
    bgcolor: cor.light,
    color: cor.dark,
    fontWeight: 700,
  };
};
